import { Component, defineAsyncComponent } from "vue";
import { Curriculum } from "@/models/Curriculum.model";
import { GoalCollection } from "@/models/GoalCollection.model";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import GoalPickerLoadFailed from "@/components/GoalPickerLoadFailed.vue";

/**
 * Explicit loader map (not a variable dynamic import): Vite would otherwise glob
 * every .vue file in src/components into its own chunk. Adding a curriculum with
 * an external picker means adding one line here — the registry contract test
 * fails loudly if an externalPicker has no matching component file.
 */
const EXTERNAL_PICKER_LOADERS: Record<string, () => Promise<any>> = {
	GoalPickerZill: () => import("@/goals/zill/GoalPickerZill.vue"),
	GoalPickerOvsg: () => import("@/goals/ovsg/GoalPickerOvsg.vue"),
	GoalPickerOpStap: () => import("@/goals/opstap/GoalPickerOpStap.vue"),
	GoalPickerGoNavigator: () => import("@/goals/gonavigator/GoalPickerGoNavigator.vue"),
};

const resolvedComponents: Record<string, Component> = {};

export function resolveExternalPickerComponent(curriculum: Curriculum): Component | null {
	const name = curriculum.externalPicker;
	if (!name) {
		return null;
	}

	const loader = EXTERNAL_PICKER_LOADERS[name];
	if (!loader) {
		console.error(`No loader registered for external goal picker "${name}"`);
		return null;
	}

	if (!resolvedComponents[name]) {
		// Vue caches a rejected async component request: without an errorComponent, a failed
		// chunk load (stale hash after a redeploy, offline) mounts nothing at all, so the host
		// never receives `opened` or `requestUnmount` and its launch spinner would spin forever.
		resolvedComponents[name] = defineAsyncComponent({
			loader,
			errorComponent: GoalPickerLoadFailed,
			timeout: 30000,
		});
	}

	return resolvedComponents[name];
}

export type FixedGoalSource =
	| { curriculum: Curriculum }
	| { collection: GoalCollection; notYetCovered: boolean };

export type GoalPickerLaunchSource =
	| { curriculum: Curriculum }
	| { collection: GoalCollection; notYetCovered?: boolean };

export type GoalPickerLaunchDescriptor =
	| { kind: "external"; component: Component; curriculum: Curriculum }
	| { kind: "drawer"; curriculum: Curriculum; fixedSource: FixedGoalSource };

/**
 * Resolves a goal source (a curriculum or a goal collection) to a launch
 * descriptor. Hosts keep exactly one piece of state — the active descriptor —
 * and render either the external picker component or the GoalSelector drawer.
 */
export function resolveGoalPickerLaunch(source: GoalPickerLaunchSource): GoalPickerLaunchDescriptor {
	if ("curriculum" in source) {
		const component = resolveExternalPickerComponent(source.curriculum);
		if (component) {
			return { kind: "external", component, curriculum: source.curriculum };
		}
		return {
			kind: "drawer",
			curriculum: source.curriculum,
			fixedSource: { curriculum: source.curriculum },
		};
	}

	// Collection launches always open the generic drawer, prefiltered to the collection.
	const curriculum = source.collection.curriculum
		?? useOrganisationsStore().getCurrentOrganisation().curriculum;

	if (!curriculum) {
		throw new Error("Cannot launch goal selector: collection and school both lack a curriculum");
	}

	return {
		kind: "drawer",
		curriculum,
		fixedSource: { collection: source.collection, notYetCovered: source.notYetCovered ?? false },
	};
}
